/* AI Transparency & Limitations — public legal page */
import React from "react";
import { useComplianceCatalog } from "@/lib/compliance";
import { useLanguage } from "@/i18n/LanguageContext";
import { Sparkles, AlertTriangle, Microscope, ShieldCheck } from "lucide-react";
import SEO from "@/components/SEO";
import AppFooter from "@/components/AppFooter";

const BULLETS_BG = [
  "AI генерира вероятностни изводи — не абсолютни истини.",
  "Качеството на изходите зависи от пълнотата на подадените от вас данни.",
  "AI може да даде непълен или неточен отговор. Винаги проверявайте със специалист.",
  "Не всички edge cases и редки заболявания са покрити.",
  "Научната обосновка (Evidence Engine) намалява, но не премахва несигурността.",
  "Моделите се обновяват; миналите резултати може да не съвпадат с новите версии.",
];
const BULLETS_EN = [
  "AI generates probabilistic outputs — not absolute truths.",
  "Output quality depends on the completeness of the data you provide.",
  "AI can be incomplete or incorrect. Always verify with a specialist.",
  "Not all edge cases and rare conditions are covered.",
  "The Evidence Engine reduces, but does not eliminate, uncertainty.",
  "Models are updated; past outputs may not match newer versions.",
];

export default function AITransparencyPage() {
  const { catalog } = useComplianceCatalog();
  const { language } = useLanguage();
  const bg = language === "bg";
  const bullets = bg ? BULLETS_BG : BULLETS_EN;

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-sky-50">
      <SEO
        title={bg ? "AI Прозрачност и ограничения — Nutramilo" : "AI Transparency & Limitations — Nutramilo"}
        description={bg ? "Как работи AI в Nutramilo, какви са ограниченията и как да интерпретирате резултатите." : "How AI works at Nutramilo, its limitations and how to interpret outputs."}
      />
      <main className="max-w-3xl mx-auto px-4 py-10" data-testid="ai-transparency-page">
        <a href="/" className="text-xs text-sky-700 hover:underline">{bg ? "← Обратно" : "← Back"}</a>
        <h1 className="text-3xl font-bold text-gray-900 mt-3 mb-2 flex items-center gap-2">
          <Sparkles className="text-purple-600" size={26} />
          {bg ? "AI Прозрачност и ограничения" : "AI Transparency & Limitations"}
        </h1>
        <p className="text-sm text-gray-600 mb-6">
          {bg
            ? "Вярваме, че потребителите заслужават да знаят как работи системата и къде са границите ѝ."
            : "We believe users deserve to know how the system works and where its limits lie."}
        </p>

        {catalog?.intended_use && (
          <section className="rounded-lg border border-sky-200 bg-sky-50 p-4 mb-6">
            <div className="text-xs font-semibold text-sky-900 uppercase tracking-wide mb-1">
              {bg ? "Предназначение" : "Intended Use"}
            </div>
            <p className="text-sm text-sky-900 leading-relaxed">{catalog.intended_use}</p>
          </section>
        )}

        <section className="mb-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
            <AlertTriangle size={18} className="text-amber-600" />
            {bg ? "Ключови ограничения" : "Key limitations"}
          </h2>
          <ul className="space-y-2 text-sm text-gray-700">
            {bullets.map((t) => (
              <li key={t} className="flex gap-2">
                <span className="text-sky-500 mt-0.5">•</span>
                <span>{t}</span>
              </li>
            ))}
          </ul>
        </section>

        <section className="mb-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
            <Microscope size={18} className="text-emerald-600" />
            {bg ? "Как намаляваме несигурността" : "How we reduce uncertainty"}
          </h2>
          <ul className="space-y-2 text-sm text-gray-700">
            <li>• {bg ? "Evidence Engine — всяко клинично твърдение се сверява с PubMed и Semantic Scholar." : "Evidence Engine — every clinical claim is checked against PubMed and Semantic Scholar."}</li>
            <li>• {bg ? "Evidence dots — визуален индикатор за силата на доказателствата (зелен/жълт/червен)." : "Evidence dots — visual indicator for evidence strength (green/amber/red)."}</li>
            <li>• {bg ? "Продължителна оценка на моделите след всяко обновяване." : "Continuous model evaluation after each update."}</li>
          </ul>
        </section>

        <section className="mb-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
            <ShieldCheck size={18} className="text-sky-600" />
            {bg ? "Как да интерпретирате резултатите" : "How to interpret outputs"}
          </h2>
          <p className="text-sm text-gray-700 leading-relaxed">
            {bg
              ? 'Третирайте всеки изход като отправна точка за разговор с вашия лекар или диетолог. Не започвайте и не спирайте лечение и не променяйте медикаменти въз основа само на AI анализ. Използвайте „Валидирано" badge-а като сигнал, че твърдението е подкрепено от научни източници.'
              : "Treat each output as a starting point for a conversation with your physician or dietitian. Do not start or stop therapy, or adjust medication, based solely on AI analysis. Use the \"Validated\" badge as a signal that the claim is supported by scientific sources."}
          </p>
        </section>

        {catalog?.liability && (
          <section className="rounded-lg border border-rose-200 bg-rose-50 p-4 text-sm text-rose-900 leading-relaxed">
            <strong className="block mb-1">{bg ? "Отговорност" : "Liability"}</strong>
            {catalog.liability}
          </section>
        )}
      </main>
      <AppFooter />
    </div>
  );
}
